import {
  ScreeningConditionOperandType,
  ScreeningConditionSet,
  ScreeningConditionSubset,
  SYSTEM_TRANSLATION_DEFAULTS,
  SystemTranslationKeys,
} from '@common/entities';

type Translate = (key: string, options?: Record<string, unknown>) => string;

function defaultTranslate(key: string, options?: Record<string, unknown>): string {
  const template = SYSTEM_TRANSLATION_DEFAULTS[key] ?? key;
  return Object.entries(options ?? {}).reduce(
    (result, [name, value]) => result.replaceAll(`{{${name}}}`, String(value)),
    template,
  );
}

/**
 * RV often models “pick one of two branches” as OR between two subsets with one line each.
 * For display we show a single “Choose N…” line and list both options together.
 */
export function shouldFlattenOrSingleOptionSubsets(conditionSet: ScreeningConditionSet): boolean {
  return (
    conditionSet.operandType === ScreeningConditionOperandType.Or &&
    conditionSet.subsets.length === 2 &&
    conditionSet.subsets.every((s) => s.conditions.length === 1)
  );
}

function intraSubsetJoinWord(operandType: ScreeningConditionOperandType): string {
  return operandType === ScreeningConditionOperandType.Or
    ? SystemTranslationKeys.common.screeningConditionOr
    : SystemTranslationKeys.common.screeningConditionAnd;
}

/** When every listed option must be selected and there is more than one, show "All"; with a single option use "1". */
function chooseQuantityLabel(min: number, conditionCount: number): string | number {
  if (conditionCount > 1 && min === conditionCount) {
    return 'All';
  }
  return min;
}

function baseChoosePhrase(
  min: number,
  subset: ScreeningConditionSubset,
  translate: Translate,
): string {
  const quantity = chooseQuantityLabel(min, subset.conditions.length);
  return translate(SystemTranslationKeys.common.screeningChooseOptions, {
    quantity:
      quantity === 'All' ? translate(SystemTranslationKeys.common.screeningAllOptions) : quantity,
  });
}

/**
 * Labels for each subset row. {@link ScreeningConditionOperandType} on each set comes from RV
 * (`ScreeningConditionOperandTypeId`) and describes how subset one combines with subset two **within**
 * that package. When multiple top-level condition sets are returned, each must be satisfied
 * independently; the lead-in on the first subset of each set after the first is **AND**.
 */
export function buildSubsetHeadings(
  conditionSets: ScreeningConditionSet[],
  translate: Translate = defaultTranslate,
): Record<number, string> {
  if (conditionSets.length === 0) return {};
  const headings: Record<number, string> = {};

  conditionSets.forEach((conditionSet, setIndex) => {
    conditionSet.subsets.forEach((subset, subsetIndex) => {
      const min = subset.minSelect !== undefined ? subset.minSelect : subset.requiredItems;
      let sectionLabel = baseChoosePhrase(min, subset, translate);

      if (setIndex > 0 && subsetIndex === 0) {
        sectionLabel = translate(
          SystemTranslationKeys.common.screeningConditionHeadingWithOperator,
          {
            operator: translate(SystemTranslationKeys.common.screeningConditionAnd),
            heading: sectionLabel,
          },
        );
      }
      if (subsetIndex > 0) {
        sectionLabel = translate(
          SystemTranslationKeys.common.screeningConditionHeadingWithOperator,
          {
            operator: translate(intraSubsetJoinWord(conditionSet.operandType)),
            heading: sectionLabel,
          },
        );
      }
      headings[subset.id] = sectionLabel;
    });
  });

  return headings;
}
